import { motion, MotionProps } from 'framer-motion';
import { forwardRef, ComponentType, FC, createElement, memo } from 'react';

// Memoize common motion components for better performance and explicitly provide types
export const MotionDiv = motion.div;
export const MotionButton = motion.button;
export const MotionSection = motion.section;
export const MotionImg = motion.img;

// Create a generic MotionComponent for consistent naming across the app
export const MotionComponent = motion.div;

// Create motion versions of any component with performance optimizations
export function createOptimizedMotionComponent<T extends object>(
  Component: ComponentType<T>
): FC<T & MotionProps> {
  // Use proper type casting through 'unknown' to avoid direct conversion errors
  const MotionComponent = memo(motion(Component) as unknown as FC<T & MotionProps>);
  return MotionComponent;
}

// Common animation variants for reuse
export const fadeInVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

export const scaleUp = {
  initial: { scale: 0.95, opacity: 0 },
  animate: { 
    scale: 1, 
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  },
  exit: {
    scale: 0.95,
    opacity: 0,
    transition: { duration: 0.4, ease: "easeIn" }
  }
};

// Simpler animations for mobile devices
export const simpleFadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.5 }
  }
};
